// Dr.Rx™ Clinical Reference Library Hub
import React from 'react';
import type { View } from '../types';

const SmallLogoIcon = () => (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="5" fill="#FACC15" />
        <line x1="24" y1="9" x2="24" y2="17" stroke="white" strokeWidth="2" />
        <circle cx="24" cy="7" r="2" fill="white" />
        <line x1="24" y1="31" x2="24" y2="39" stroke="white" strokeWidth="2" />
        <circle cx="24" cy="41" r="2" fill="white" />
        <line x1="9" y1="24" x2="17" y2="24" stroke="white" strokeWidth="2" />
        <circle cx="7" cy="24" r="2" fill="white" />
        <line x1="31" y1="24" x2="39" y2="24" stroke="white" strokeWidth="2" />
        <circle cx="41" cy="24" r="2" fill="white" />
    </svg>
);

const ArrowRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-400 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
    </svg>
);

const DrugIndexIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
);

const GuidelinesIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
);

const EvidenceSearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);

const StudyModeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
    </svg>
);

const CORE_LIBRARY_ITEMS = [
    {
        name: 'Drug Index',
        description: 'Browse verified monographs for the Top 100 US medications plus your custom-added clinical formulary.',
        badge: 'Top 100 + Custom',
        icon: <DrugIndexIcon />,
        view: 'drug-index' as View,
    },
    {
        name: 'Clinical Guideline Summaries',
        description: 'Rapid point-of-care summaries of ACC/AHA, IDSA, ADA, CHEST, GINA, and KDIGO clinical practice guidelines.',
        badge: 'ACC / IDSA / ADA',
        icon: <GuidelinesIcon />,
        view: 'clinical-guides' as View,
    },
    {
        name: 'AI-Powered Evidence Search',
        description: 'Synthesize primary medical literature, clinical trial endpoints, and FDA safety communications.',
        badge: 'PubMed / FDA',
        icon: <EvidenceSearchIcon />,
        view: 'evidence-search' as View,
    },
    {
        name: 'Study Mode: Top 200 Drugs',
        description: 'Master high-yield pharmacology with interactive 3D flashcards and customizable NAPLEX/MPJE quizzes.',
        badge: 'Flashcards & Quiz',
        icon: <StudyModeIcon />,
        view: 'study-mode' as View,
    },
];

interface LibraryCardProps {
    name: string;
    description: string;
    icon: React.ReactNode;
    onClick: () => void;
    badge?: string;
}

const LibraryCard: React.FC<LibraryCardProps> = ({ name, description, icon, onClick, badge }) => (
    <div
        onClick={onClick}
        className="group bg-[#15243E] p-6 rounded-2xl border border-blue-900/60 flex flex-col justify-between transition-all duration-300 hover:border-yellow-400/80 hover:bg-[#1E2E4A] hover:-translate-y-1 cursor-pointer shadow-lg"
    >
        <div>
            <div className="flex items-start justify-between gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#101D35] border border-blue-800/50 text-yellow-400 group-hover:border-yellow-400/50 transition-colors">
                    {icon}
                </div>
                {badge && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-yellow-400/15 text-yellow-300 border border-yellow-400/30">
                        {badge}
                    </span>
                )}
            </div>
            <h3 className="font-bold text-lg text-white group-hover:text-yellow-300 transition-colors">{name}</h3>
            <p className="text-sm text-gray-400 mt-1.5 leading-relaxed">{description}</p>
        </div>
        <div className="mt-6 pt-3 border-t border-blue-900/40 flex items-center justify-between">
            <span className="text-xs font-bold text-yellow-400 uppercase tracking-wider">Open Module</span>
            <ArrowRightIcon />
        </div>
    </div>
);

interface ToolsProps {
    onNavigate: (view: View) => void;
    onSelectDrug?: (drugId: string) => void;
    setActiveTool?: (tool: string | null) => void;
    onSelectGuide?: (topic: string, source: string) => void;
    uid?: string | null;
}

const Tools: React.FC<ToolsProps> = ({ onNavigate }) => {
    return (
        <div className="py-6">
            <header className="mb-8 flex justify-between items-start">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">Clinical Reference Library</h1>
                    <p className="text-gray-400 mt-2 text-sm sm:text-base max-w-2xl">
                        Verified drug monographs, clinical practice guideline summaries, literature synthesis, and interactive board preparation.
                    </p>
                </div>
                <button
                    onClick={() => onNavigate('dashboard')}
                    className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-all cursor-pointer hover:scale-105"
                    title="Return to Dashboard"
                >
                    <SmallLogoIcon />
                    <span className="text-sm font-bold text-white hidden sm:inline">
                        Dr.<span className="text-yellow-400">Rx</span>
                        <sup className="text-xs -top-2">TM</sup>
                    </span>
                </button>
            </header>

            <section className="mb-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {CORE_LIBRARY_ITEMS.map(item => (
                        <LibraryCard
                            key={item.name}
                            name={item.name}
                            description={item.description}
                            icon={item.icon}
                            badge={item.badge}
                            onClick={() => onNavigate(item.view)}
                        />
                    ))}
                </div>
            </section>

            {/* Direct CTA to AI Clinical Co-Pilot & All 40+ Calculators */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div
                    onClick={() => onNavigate('chat')}
                    className="bg-gradient-to-br from-[#1E2E4A] to-[#15243E] p-6 rounded-2xl border border-yellow-400/40 hover:border-yellow-400 transition-all cursor-pointer flex items-center justify-between gap-4 shadow-xl"
                >
                    <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-yellow-400">Point-of-Care Engine</span>
                        <h2 className="text-xl font-bold text-white mt-1">Launch AI Clinical Co-Pilot</h2>
                        <p className="text-xs text-gray-300 mt-1">
                            Role-calibrated dosing, CYP/P-gp interaction analysis, and inline monograph lookup.
                        </p>
                    </div>
                    <span className="bg-yellow-400 text-gray-950 font-bold text-xs px-4 py-2.5 rounded-xl whitespace-nowrap shadow-md">
                        Open Chat →
                    </span>
                </div>

                <div
                    onClick={() => onNavigate('all-tools')}
                    className="bg-gradient-to-br from-[#1E2E4A] to-[#15243E] p-6 rounded-2xl border border-blue-800/60 hover:border-yellow-400/60 transition-all cursor-pointer flex items-center justify-between gap-4 shadow-xl"
                >
                    <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-blue-300">40+ Clinical Utilities</span>
                        <h2 className="text-xl font-bold text-white mt-1">Clinical Calculators & Tools</h2>
                        <p className="text-xs text-gray-300 mt-1">
                            Renal dosing, IV Y-site compatibility, MME, vancomycin PK, anticoag bridging, and law.
                        </p>
                    </div>
                    <span className="bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs px-4 py-2.5 rounded-xl whitespace-nowrap shadow-md">
                        Browse Tools →
                    </span>
                </div>
            </section>
        </div>
    );
};

export default Tools;